import type { ComponentProps, ReactNode } from "react"
import { memo } from "react"

import { cn } from "@productlane/lib/src/browser"

import {
	NAV_TOGGLE_CLEARANCE,
	TOP_BAR_HEIGHT,
	TOP_BAR_PADDING,
	TOP_BAR_PADDING_CLEARING_NAV_TOGGLE,
} from "./top-bar-tokens"

interface PageHeaderProps extends Omit<ComponentProps<"div">, "title"> {
	title?: ReactNode
	actions?: ReactNode
	leading?: ReactNode
	tabs?: ReactNode
	children?: ReactNode
	onTitleClick?: () => void
	titleClassName?: string
	/** The page's leftmost bar, which makes room for the hidden sidebar's toggle. */
	clearsNavToggle?: boolean
}
export const PageHeader = memo(
	({
		title,
		tabs,
		actions,
		leading,
		className,
		children,
		onTitleClick,
		titleClassName,
		clearsNavToggle,
		...props
	}: PageHeaderProps) => {
		const titleComponent = title && (
			<h1 className={cn("min-w-0 text-base font-semibold", titleClassName)}>
				{title}
			</h1>
		)

		return (
			<>
				<div
					className={cn(
						`relative z-10 flex ${TOP_BAR_HEIGHT} w-full select-none flex-row items-center justify-between gap-4 border-b border-gray-200 bg-white py-2 pl-2 pr-3 sm:flex-row sm:pl-[17.5px] lg:gap-0 dark:border-gray-700/80 dark:bg-gray-900`,
						className,
					)}
					translate="no"
					{...props}
				>
					{tabs && <div className="hidden lg:block">{tabs}</div>}
					<div
						className={cn(
							"flex min-w-0 flex-row items-center gap-2 max-sm:gap-3",
							clearsNavToggle && NAV_TOGGLE_CLEARANCE,
						)}
					>
						{leading}
						{onTitleClick ? (
							// Negative margins cancel the hover pill's padding so the title
							// text sits exactly where a non-clickable title does.
							<button
								type="button"
								onClick={onTitleClick}
								className="-mx-1.5 -my-1 min-w-0 rounded-md px-1.5 py-1 text-left hover:bg-gray-100 dark:hover:bg-gray-800"
							>
								{titleComponent}
							</button>
						) : (
							titleComponent
						)}
					</div>
					{children}
					{actions}
				</div>
				{tabs && (
					<div className="relative z-10 flex h-10 max-h-10 min-h-10 w-full select-none flex-row items-center justify-between gap-4 border-b border-gray-200 bg-gray-25 py-2 pl-3 pr-3 sm:flex-row lg:hidden lg:gap-0 dark:border-gray-700/80 dark:bg-gray-900">
						{tabs}
					</div>
				)}
			</>
		)
	},
)

export const DetailTopBar = memo(
	({
		className,
		clearsNavToggle,
		...props
	}: ComponentProps<"div"> & { clearsNavToggle?: boolean }) => {
		return (
			<div
				className={cn(
					"flex min-h-12 items-center gap-2 border-b border-gray-200 py-2 dark:border-gray-700/80",
					clearsNavToggle
						? TOP_BAR_PADDING_CLEARING_NAV_TOGGLE
						: TOP_BAR_PADDING,
					className,
				)}
				{...props}
			/>
		)
	},
)

// Shared separator for every dashboard breadcrumb. Sized down from the crumb
// text so a full-height glyph at text weight doesn't compete with the labels.
export const BreadcrumbSlash = memo(({ className }: { className?: string }) => (
	<span
		aria-hidden
		className={cn(
			"flex-none select-none text-[0.8125rem] font-normal text-tertiary",
			className,
		)}
	>
		/
	</span>
))

interface DetailBreadcrumbProps {
	parentLabel: string
	onParentClick: () => void
	currentIcon?: ReactNode
	// Linear's issue identifier, which stays whole while the label truncates.
	currentPrefix?: string | null
	currentLabel: string
}

export const DetailBreadcrumb = memo(
	({
		parentLabel,
		onParentClick,
		currentIcon,
		currentPrefix,
		currentLabel,
	}: DetailBreadcrumbProps) => (
		// One size, one weight, one color across the trail: the crumbs read as a
		// single line rather than a hierarchy. The negative margin cancels the
		// first crumb's hover pill so the text sits at the bar's own inset.
		<nav
			aria-label="Breadcrumb"
			className="-ml-1.5 flex min-w-0 items-center gap-1 text-sm font-medium text-primary"
		>
			<button
				type="button"
				onClick={onParentClick}
				className="rounded px-1.5 py-1 hover:bg-gray-100 dark:hover:bg-gray-800"
			>
				{parentLabel}
			</button>
			<BreadcrumbSlash className="text-primary" />
			<div className="flex min-w-0 items-center gap-1.5 px-1.5 py-1">
				{currentIcon}
				{currentPrefix && <span className="flex-none">{currentPrefix}</span>}
				<span className="truncate">{currentLabel}</span>
			</div>
		</nav>
	),
)
